import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateDemoEvaluations, clearAllData } from '@/services/demo-data';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EvaluationCard } from '@/components/EvaluationCard';
import { FilterBar, type SortOption } from '@/components/FilterBar';
import { Plus, Search, Database, Trash2, X } from 'lucide-react';
import { useEvaluations } from '@/hooks';
import { EvaluationStatus } from '@/types/evaluation.types';

// Search history management
const SEARCH_HISTORY_KEY = 'evaluation-search-history';
const MAX_SEARCH_HISTORY = 5;

function getSearchHistory(): string[] {
  try {
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch {
    return [];
  }
}

function saveSearchHistory(query: string) {
  if (!query.trim()) return;

  try {
    const history = getSearchHistory();
    const updated = [query, ...history.filter(q => q !== query)].slice(0, MAX_SEARCH_HISTORY);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
}

function clearSearchHistory() {
  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

export function Home() {
  const { t } = useTranslation('home');
  const { data: evaluations = [], isLoading } = useEvaluations();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // State management
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>(getSearchHistory());

  // Parse URL params for filters and sort
  const statusParam = searchParams.get('status');
  const initialStatuses = statusParam
    ? statusParam.split(',').filter(s => Object.values(EvaluationStatus).includes(s as EvaluationStatus)) as EvaluationStatus[]
    : Object.values(EvaluationStatus);

  const [selectedStatuses, setSelectedStatuses] = useState<EvaluationStatus[]>(initialStatuses);
  const [sortBy, setSortBy] = useState<SortOption>(
    (searchParams.get('sort') as SortOption) || 'dateNewest'
  );

  // Debounce search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (searchQuery.trim()) {
        saveSearchHistory(searchQuery);
        setSearchHistory(getSearchHistory());
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync URL params with state
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchQuery) {
      params.set('search', searchQuery);
    }

    if (selectedStatuses.length !== Object.values(EvaluationStatus).length) {
      params.set('status', selectedStatuses.join(','));
    }

    if (sortBy !== 'dateNewest') {
      params.set('sort', sortBy);
    }

    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedStatuses, sortBy, setSearchParams]);

  // Filter, search, and sort evaluations
  const filteredEvaluations = useMemo(() => {
    let result = evaluations;

    // Filter by status
    if (selectedStatuses.length < Object.values(EvaluationStatus).length) {
      result = result.filter(e => selectedStatuses.includes(e.status));
    }

    // Search across multiple fields
    if (debouncedSearchQuery.trim()) {
      const lowerQuery = debouncedSearchQuery.toLowerCase();
      result = result.filter(evaluation => {
        // Search in evaluation name
        if (evaluation.name.toLowerCase().includes(lowerQuery)) {
          return true;
        }

        // Search in solution names
        if (evaluation.targetSolutions.some(s =>
          s.solutionName.toLowerCase().includes(lowerQuery) ||
          s.vendorName?.toLowerCase().includes(lowerQuery)
        )) {
          return true;
        }

        // Search in participant names and emails
        if (evaluation.participants.some(p =>
          p.name.toLowerCase().includes(lowerQuery) ||
          p.email.toLowerCase().includes(lowerQuery)
        )) {
          return true;
        }

        return false;
      });
    }

    // Sort
    const sorted = [...result];
    switch (sortBy) {
      case 'nameAsc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'nameDesc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'dateNewest':
        return sorted.sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime());
      case 'dateOldest':
        return sorted.sort((a, b) => a.createdDate.getTime() - b.createdDate.getTime());
      case 'targetDateSoonest':
        return sorted.sort((a, b) => {
          if (!a.targetCompletionDate) return 1;
          if (!b.targetCompletionDate) return -1;
          return a.targetCompletionDate.getTime() - b.targetCompletionDate.getTime();
        });
      case 'targetDateLatest':
        return sorted.sort((a, b) => {
          if (!a.targetCompletionDate) return 1;
          if (!b.targetCompletionDate) return -1;
          return b.targetCompletionDate.getTime() - a.targetCompletionDate.getTime();
        });
      case 'solutionCountHigh':
        return sorted.sort((a, b) => b.targetSolutions.length - a.targetSolutions.length);
      case 'solutionCountLow':
        return sorted.sort((a, b) => a.targetSolutions.length - b.targetSolutions.length);
      default:
        return sorted;
    }
  }, [evaluations, selectedStatuses, debouncedSearchQuery, sortBy]);

  // Event handlers
  const handleNewEvaluation = () => {
    navigate('/evaluation/new');
  };

  const handleStatusToggle = (status: EvaluationStatus) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        // Don't allow deselecting all statuses
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleClearFilters = () => {
    setSelectedStatuses(Object.values(EvaluationStatus));
    setSortBy('dateNewest');
    setSearchQuery('');
  };

  const hasActiveFilters =
    selectedStatuses.length !== Object.values(EvaluationStatus).length ||
    sortBy !== 'dateNewest';

  const handleSearchSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSearchSuggestions(false);
  };

  const handleClearSearchHistory = () => {
    clearSearchHistory();
    setSearchHistory([]);
  };

  const handleLoadDemoData = () => {
    generateDemoEvaluations();
    alert(t('demo.samplesLoaded'));
    window.location.reload();
  };

  const handleClearAllData = () => {
    if (window.confirm('Are you sure you want to delete all evaluations? This cannot be undone.')) {
      clearAllData();
      alert(t('demo.dataCleared'));
      window.location.reload();
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">{t('title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('subtitle')}
          </p>
        </div>
        <Button onClick={handleNewEvaluation} size="lg" className="sm:w-auto">
          <Plus className="mr-2 h-5 w-5" />
          {t('newEvaluation')}
        </Button>
      </div>

      {/* Search and Filter Bar - Single Row */}
      {evaluations.length > 0 && (
        <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Enhanced Search Input with Suggestions */}
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchHistory.length > 0 && setShowSearchSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
              className="pl-10 pr-10 h-9"
              aria-label={t('searchPlaceholder')}
              aria-expanded={showSearchSuggestions}
              aria-controls="search-suggestions"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t('searchClear')}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}

            {/* Search Suggestions Dropdown */}
            {showSearchSuggestions && searchHistory.length > 0 && !searchQuery && (
              <div
                id="search-suggestions"
                className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md z-10 py-2"
                role="listbox"
              >
                <div className="px-3 py-1 text-xs font-medium text-muted-foreground flex items-center justify-between">
                  <span>{t('searchHistory.recent')}</span>
                  <button
                    onClick={handleClearSearchHistory}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={t('searchHistory.clear')}
                  >
                    {t('searchHistory.clear')}
                  </button>
                </div>
                {searchHistory.map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleSearchSuggestionClick(query)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                    role="option"
                  >
                    <Search className="inline h-3 w-3 mr-2 text-muted-foreground" aria-hidden="true" />
                    {query}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter and Sort Controls */}
          <FilterBar
            selectedStatuses={selectedStatuses}
            onStatusToggle={handleStatusToggle}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="rounded-full bg-muted p-6 mb-4 animate-pulse">
            <Search className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">Loading...</h2>
          <p className="text-muted-foreground">Fetching your evaluations</p>
        </div>
      ) : /* Evaluations Grid */
      evaluations.length === 0 ? (
        // Empty State
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="rounded-full bg-muted p-6 mb-4">
            <Search className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">{t('empty.title')}</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t('empty.description')}
          </p>
          <Button onClick={handleNewEvaluation} size="lg">
            <Plus className="mr-2 h-5 w-5" />
            {t('empty.action')}
          </Button>
        </div>
      ) : filteredEvaluations.length === 0 ? (
        // No Results State
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="rounded-full bg-muted p-6 mb-4">
            <Search className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">{t('noResults.title')}</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t('noResults.description')}
          </p>
          <Button variant="outline" onClick={() => setSearchQuery('')}>
            {t('common:actions.search', 'Clear Search')}
          </Button>
        </div>
      ) : (
        // Evaluations Grid - Responsive: 1 col mobile, 2 cols tablet, 3 cols desktop
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvaluations.map((evaluation) => (
            <EvaluationCard key={evaluation.id} evaluation={evaluation} />
          ))}
        </div>
      )}

      {/* Results Count */}
      {evaluations.length > 0 && filteredEvaluations.length > 0 && (
        <div
          className="mt-8 text-center text-sm text-muted-foreground"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Showing {filteredEvaluations.length} of {evaluations.length} evaluation{evaluations.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Demo Controls - Inconspicuous footer section */}
      <div className="mt-16 pt-8 border-t border-border/50">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLoadDemoData}
            className="text-xs text-muted-foreground hover:text-foreground"
            aria-label={t('demo.loadSamplesDescription')}
          >
            <Database className="mr-2 h-3 w-3" aria-hidden="true" />
            {t('demo.loadSamples')}
          </Button>
          <span className="text-xs text-muted-foreground/50">•</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAllData}
            className="text-xs text-muted-foreground hover:text-destructive"
            aria-label={t('demo.clearAllDescription')}
          >
            <Trash2 className="mr-2 h-3 w-3" aria-hidden="true" />
            {t('demo.clearAll')}
          </Button>
        </div>
      </div>
    </div>
  );
}
